import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { fetchLandlordInvoicePdfUrl, fetchLandlordInvoices } from "../../api/landlordApi";
import { formatDateOnly } from "../../utils/datetime";
import {
  formatInvoiceAmount,
  invoiceStatusLabelDe,
  invoiceStatusStyle,
  pdfActionLabel,
  PDF_UNAVAILABLE_DE,
} from "../../utils/portalInvoiceView";

/**
 * The landlord's invoices — all of them, from wherever they came from.
 *
 * After INV-RETIRE.3f this list mixes invoices Vantio issued itself with invoices
 * mirrored from the billing provider, and the landlord is not told which is which.
 * The distinction is an operator's concern; surfacing it would make two eras of
 * the same tenancy look like two kinds of income.
 *
 * The page consumes only what the portal contract offers — `portal_id`, `status`,
 * `period_label`, `pdf_available`, plus the property/unit/tenant context the
 * landlord list has always carried — and reconstructs nothing. Whether this
 * landlord may see a given invoice was decided by the backend against their own
 * ownership chain; asking any part of it again here would be a second authority.
 */

const HEADER_CELL = { padding: "12px 8px", color: "#64748B" };
const BODY_CELL = { padding: "12px 8px" };
//: Keeps the columns legible while the existing overflow wrapper handles narrow
//: screens by scrolling rather than by crushing cells into each other.
const TABLE = { width: "100%", minWidth: "1040px", borderCollapse: "collapse", fontSize: "14px" };
const NOWRAP = { whiteSpace: "nowrap" };

const PDF_BUTTON = {
  padding: "6px 12px",
  cursor: "pointer",
  border: "1px solid #E5E7EB",
  borderRadius: "6px",
  background: "transparent",
  color: "#EA580C",
  fontWeight: 600,
  fontSize: "13px",
};
const PDF_BUTTON_BUSY = { ...PDF_BUTTON, cursor: "progress", color: "#64748B" };

function LandlordInvoicesPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  //: Per-invoice, not per-page. One slow document must not freeze the other rows,
  //: and a second click on the SAME row while its link is being signed must not
  //: produce a second request.
  const [pdfBusy, setPdfBusy] = useState(() => new Set());

  useEffect(() => {
    setLoading(true);
    setError("");
    fetchLandlordInvoices()
      .then((data) => setList(Array.isArray(data) ? data : []))
      .catch((e) => setError(e.message || "Rechnungen konnten nicht geladen werden."))
      .finally(() => setLoading(false));
  }, []);

  const handleOpenPdf = useCallback(async (portalId) => {
    if (!portalId) return;
    let alreadyRunning = false;
    setPdfBusy((prev) => {
      if (prev.has(portalId)) {
        alreadyRunning = true;
        return prev;
      }
      const next = new Set(prev);
      next.add(portalId);
      return next;
    });
    if (alreadyRunning) return;

    try {
      const result = await fetchLandlordInvoicePdfUrl(portalId);
      if (result && result.url) {
        // Opened after the await, which is the pattern this repo already uses for
        // signed downloads. `noopener,noreferrer` keeps the new tab from reaching
        // back through `window.opener`.
        window.open(result.url, "_blank", "noopener,noreferrer");
      } else {
        toast.error(PDF_UNAVAILABLE_DE);
      }
    } catch {
      // Deliberately ignores whatever the caught error says. The list keeps its
      // own error channel; a failed document must not empty the table or turn the
      // page into an error state, because every other invoice is still fine.
      toast.error(PDF_UNAVAILABLE_DE);
    } finally {
      setPdfBusy((prev) => {
        const next = new Set(prev);
        next.delete(portalId);
        return next;
      });
    }
  }, []);

  const tenantDisplay = (inv) =>
    (inv.tenant_name && inv.tenant_name.trim()) ? inv.tenant_name : (inv.tenant_email || "—");
  const unitDisplay = (inv) => inv.unit_title || inv.unit_name || inv.unit_id || "—";
  const propertyDisplay = (inv) => inv.property_title || inv.property_id || "—";

  if (loading) return <p style={{ color: "#64748B" }}>Lade …</p>;
  if (error) return <p style={{ color: "#B91C1C" }}>{error}</p>;

  return (
    <div>
      <h2 style={{ fontSize: "24px", fontWeight: 800, margin: "0 0 16px 0", color: "#0F172A" }}>
        Rechnungen
      </h2>
      {list.length === 0 ? (
        <p style={{ color: "#64748B" }}>Keine Rechnungen vorhanden.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={TABLE}>
            <thead>
              <tr style={{ borderBottom: "2px solid #E5E7EB", textAlign: "left" }}>
                <th style={HEADER_CELL}>Rechnungsnummer</th>
                <th style={HEADER_CELL}>Periode</th>
                <th style={HEADER_CELL}>Betrag</th>
                <th style={HEADER_CELL}>Fällig am</th>
                <th style={HEADER_CELL}>Status</th>
                <th style={HEADER_CELL}>Mieter</th>
                <th style={HEADER_CELL}>Objekt</th>
                <th style={HEADER_CELL}>Einheit</th>
                <th style={HEADER_CELL}>Beleg</th>
              </tr>
            </thead>
            <tbody>
              {list.map((inv) => {
                const busy = pdfBusy.has(inv.portal_id);
                return (
                  // portal_id, not id: it is the one identifier that is unique
                  // across both sources, and it is what the document action
                  // addresses. Keying on `id` would collide the day a native
                  // integer and a mirrored row met in the same list.
                  <tr key={inv.portal_id} style={{ borderBottom: "1px solid #F1F5F9" }}>
                    <td style={{ ...BODY_CELL, ...NOWRAP, fontWeight: 600 }}>
                      {inv.invoice_number || "—"}
                    </td>
                    {/* Only what the backend attributed. A period guessed from
                        issue_date or from the invoice number would be a claim
                        about which month is billed that nobody verified. */}
                    <td style={BODY_CELL}>{inv.period_label || ""}</td>
                    <td style={{ ...BODY_CELL, ...NOWRAP }}>
                      {formatInvoiceAmount(inv.amount, inv.currency)}
                    </td>
                    <td style={{ ...BODY_CELL, ...NOWRAP }}>{formatDateOnly(inv.due_date)}</td>
                    <td style={{ ...BODY_CELL, ...NOWRAP, ...invoiceStatusStyle(inv.status) }}>
                      {invoiceStatusLabelDe(inv.status)}
                    </td>
                    <td style={BODY_CELL}>{tenantDisplay(inv)}</td>
                    <td style={BODY_CELL}>{propertyDisplay(inv)}</td>
                    <td style={BODY_CELL}>{unitDisplay(inv)}</td>
                    <td style={{ ...BODY_CELL, ...NOWRAP }}>
                      {inv.pdf_available === true ? (
                        <button
                          type="button"
                          aria-label={pdfActionLabel(inv.invoice_number)}
                          disabled={busy}
                          onClick={() => handleOpenPdf(inv.portal_id)}
                          style={busy ? PDF_BUTTON_BUSY : PDF_BUTTON}
                        >
                          {busy ? "Wird geöffnet …" : "PDF öffnen"}
                        </button>
                      ) : (
                        // No button at all rather than one that is bound to fail:
                        // the backend already said no path exists.
                        <span style={{ color: "#64748B" }}>Nicht verfügbar</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default LandlordInvoicesPage;
